import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../constants/Languages';
import {
  MARKETING_DEFAULT_LANGUAGE,
  localizeMarketingPath,
  parseMarketingPath,
} from '../../constants/marketingSeo';

/**
 * Keeps the UI language and the marketing URL in step (main domain only):
 * - A prefixed URL (`/en/...`) sets the language, so each URL always serves one language.
 * - An unprefixed marketing URL visited while another language is active (stored choice
 *   or in-app selection) is replaced by that language's URL. Crawlers have no stored
 *   choice, so they always get the page they requested.
 * - Switching language anywhere while on a marketing page moves to that language's URL.
 */
const MarketingLanguageSync = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { lang: urlLang, basePath, prefixed, isPage } = parseMarketingPath(location.pathname);
    if (!prefixed && !isPage) {
      return;
    }

    const activeLang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    const suffix = `${location.search}${location.hash}`;

    if (prefixed && urlLang === MARKETING_DEFAULT_LANGUAGE) {
      const goToCanonical = () => {
        navigate(`${localizeMarketingPath(basePath, MARKETING_DEFAULT_LANGUAGE)}${suffix}`, { replace: true });
      };
      if (activeLang !== urlLang) {
        i18n.changeLanguage(urlLang).then(goToCanonical);
        return;
      }
      goToCanonical();
      return;
    }

    if (!prefixed && activeLang !== MARKETING_DEFAULT_LANGUAGE) {
      navigate(`${localizeMarketingPath(basePath, activeLang)}${suffix}`, { replace: true });
      return;
    }

    if (activeLang !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
  }, [location.pathname, location.search, location.hash, i18n, navigate]);

  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      const { lang: urlLang, basePath, prefixed, isPage } = parseMarketingPath(window.location.pathname);
      const nextLang = normalizeLanguage(lng);
      if ((prefixed || isPage) && nextLang !== urlLang) {
        navigate(`${localizeMarketingPath(basePath, nextLang)}${window.location.search}${window.location.hash}`);
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [i18n, navigate]);

  return null;
};

export default MarketingLanguageSync;
